import React, { useEffect, useState } from 'react';
import { x } from '@xstyled/styled-components';
import { Chapter, Sequence, TrainingState, useTrainingStore } from '../state';
import { NavItem } from './NavItem';
import { select } from '~/lib/select';

const selector = (s: TrainingState) =>
  select(s, 'items', 'activeItemPosition', 'status');

type ChapterWithChildren = Chapter & { children: Sequence[] };
type OrganizedItem = ChapterWithChildren | Sequence;

const ListRenderer = ({
  level,
  items,
  openChapter,
  activeItemPos,
  setOpenChapter
}: {
  level: number;
  items: OrganizedItem[];
  openChapter: string;
  activeItemPos: number;
  setOpenChapter: (id: string) => void;
}) => {
  const getContent = () => {
    const content = items.map((item: OrganizedItem) => (
      <li key={item.id}>
        <NavItem
          item={item}
          expanded={item.id === openChapter}
          activeItemPosition={activeItemPos}
          onClick={() => {
            if (item.kind === 'chapter') {
              setOpenChapter(item.id);
            }
          }}
        />
        {(item as ChapterWithChildren).children && item.id === openChapter && (
          <ListRenderer
            level={1}
            items={(item as ChapterWithChildren).children}
            openChapter={openChapter}
            activeItemPos={activeItemPos}
            setOpenChapter={setOpenChapter}
          />
        )}
      </li>
    ));
    return content;
  };

  return level == 0 ? (
    <x.ol p="sm" m={0}>
      {getContent()}
    </x.ol>
  ) : (
    <x.ul p="sm" m={0}>
      {getContent()}
    </x.ul>
  );
};

export const TableOfContent = () => {
  const { items, activeItemPosition, status } = useTrainingStore(selector);
  const [activeItemPos, setActiveItemPos] = useState(activeItemPosition || 0);

  if (status === 'completed' && activeItemPos < 0 && items) {
    setActiveItemPos(items[items.length - 1]['position']);
  }

  const [openChapter, setOpenChapter] = useState('');

  useEffect(() => {
    if (!items || activeItemPos < 0) return;
    const item = items.find(i => i.position === activeItemPos) as Sequence;
    setOpenChapter(item.chapterId);
  }, [items, activeItemPos]);

  const organizedList = items.reduce((acc: ChapterWithChildren[], item) => {
    if (!(item as Sequence).chapterId) {
      // It's a chapter
      acc.push({ ...item, children: [] } as ChapterWithChildren);
    } else {
      // It's a sequence, find its chapter and add it as a child
      const chapter = acc.find(
        chap => chap.id === (item as Sequence).chapterId
      );
      if (chapter) {
        chapter?.children?.push(item as Sequence);
      }
    }
    return acc;
  }, []);

  return (
    <ListRenderer
      level={0}
      items={organizedList}
      activeItemPos={activeItemPos}
      openChapter={openChapter}
      setOpenChapter={setOpenChapter}
    />
  );
};
